import { type ReactNode } from 'react';

import type { WorkspaceCopy } from '../../merchant/copy.js';
import type {
  MerchantAnalysisProgress,
  MerchantAnalysisStepKey,
} from '../../merchant/api.js';
import { formatTime } from '../../merchant/model.js';
import { localizeMerchantFacingText } from '../../merchant/shared/merchantFacingText.js';
import { Button, StageList, type StageItem } from '../../merchant/ui/primitives.js';

type Localized = { readonly en: string; readonly ar: string };

/** The six server steps, in the merchant's words. */
export const ANALYSIS_STEP_CONTENT: Readonly<
  Record<
    MerchantAnalysisStepKey,
    {
      readonly title: Localized;
      readonly desc: Localized;
      readonly checking: readonly Localized[];
    }
  >
> = {
  business: {
    title: { en: 'Understanding your business', ar: 'فهم نشاطك التجاري' },
    desc: {
      en: 'Reading the sources you confirmed, so everything after this stays specific to your business.',
      ar: 'نقرأ المصادر اللي أكّدتها عشان يبقى كل اللي بعدها خاص بنشاطك.',
    },
    checking: [
      { en: 'The business sources you confirmed', ar: 'مصادر نشاطك اللي أكّدتها' },
      { en: 'Your products and offers', ar: 'منتجاتك وعروضك' },
      { en: 'How your business describes itself', ar: 'كيف يوصف نشاطك نفسه' },
    ],
  },
  market: {
    title: { en: 'Mapping your market', ar: 'رسم خريطة سوقك' },
    desc: {
      en: 'Placing your business in the market and categories it actually competes in.',
      ar: 'نحدّد موقع نشاطك في السوق والفئات اللي ينافس فيها فعلًا.',
    },
    checking: [
      {
        en: 'The market and categories you sell in',
        ar: 'السوق والفئات اللي تبيع فيها',
      },
      {
        en: 'Public demand signals in short-form content',
        ar: 'إشارات الطلب العامة في المحتوى القصير',
      },
      { en: 'Which categories are worth comparing', ar: 'الفئات اللي تستاهل المقارنة' },
    ],
  },
  competitors: {
    title: {
      en: 'Finding relevant public content',
      ar: 'البحث عن المحتوى العام المرتبط',
    },
    desc: {
      en: 'Searching for public content that is genuinely relevant to your business and audience.',
      ar: 'ندوّر على محتوى عام مرتبط فعلًا بنشاطك وجمهورك.',
    },
    checking: [
      {
        en: 'Arabic and English searches based on your confirmed business',
        ar: 'عمليات بحث عربية وإنجليزية مبنية على نشاطك المؤكد',
      },
      {
        en: 'Content relevant to your topics, market and audience',
        ar: 'محتوى مرتبط بمواضيعك وسوقك وجمهورك',
      },
      {
        en: 'Public sources with evidence we can verify',
        ar: 'مصادر عامة بأدلة نقدر نتحقق منها',
      },
    ],
  },
  content: {
    title: { en: 'Reviewing relevant content', ar: 'مراجعة المحتوى المرتبط' },
    desc: {
      en: 'Reviewing and ranking the strongest public examples found for your brief.',
      ar: 'نراجع ونرتّب أقوى الأمثلة العامة اللي لقيناها لملخصك.',
    },
    checking: [
      {
        en: 'Public posts and pages from relevant sources',
        ar: 'منشورات وصفحات عامة من مصادر مرتبطة',
      },
      {
        en: 'How recent and how frequent that content is',
        ar: 'كم هو حديث ذاك المحتوى وكم يتكرر',
      },
      {
        en: 'Which content examples are worth studying',
        ar: 'أمثلة المحتوى اللي تستاهل الدراسة',
      },
    ],
  },
  patterns: {
    title: {
      en: 'Studying content patterns and signals',
      ar: 'دراسة أنماط المحتوى وإشاراته',
    },
    desc: {
      en: 'Studying what those examples have in common, and which audience signals matter for you.',
      ar: 'ندرس وش تشترك فيه تلك الأمثلة، ووش الإشارات المهمة لجمهورك.',
    },
    checking: [
      {
        en: 'Hooks and formats that repeat across examples',
        ar: 'المداخل والصيغ اللي تتكرر في الأمثلة',
      },
      {
        en: 'Audience signals around those examples',
        ar: 'إشارات الجمهور حول تلك الأمثلة',
      },
      { en: 'What is specific to your categories', ar: 'اللي يخص فئاتك تحديدًا' },
    ],
  },
  recommendations: {
    title: { en: 'Preparing recommendations', ar: 'إعداد التوصيات' },
    desc: {
      en: 'Turning what we found into recommendations you can act on.',
      ar: 'نحوّل اللي لقيناه إلى توصيات تقدر تشتغل عليها.',
    },
    checking: [
      { en: 'Which opportunities fit your business', ar: 'الفرص اللي تناسب نشاطك' },
      {
        en: 'What each recommendation needs to be useful',
        ar: 'وش تحتاجه كل توصية عشان تكون مفيدة',
      },
      { en: 'How we explain the reasoning to you', ar: 'كيف نوضّح لك المنطق وراها' },
    ],
  },
};

const EVIDENCE_KIND: Readonly<Record<'merchant' | 'public' | 'analysis', Localized>> = {
  merchant: { en: 'Merchant source', ar: 'مصدر النشاط' },
  public: { en: 'Public evidence', ar: 'دليل عام' },
  analysis: { en: 'Analysis', ar: 'تحليل' },
};

/**
 * The wireframe's stage list on the server's six steps. Completed steps
 * carry the findings the server saved for them; a failed step carries the
 * retry the server authorised, and nothing invents a checkpoint.
 */
export function AnalysisStages({
  progress,
  copy,
  language,
  onRetry,
  working = false,
}: {
  readonly progress: MerchantAnalysisProgress;
  readonly copy: WorkspaceCopy;
  readonly language: 'en' | 'ar';
  readonly onRetry?: (key: MerchantAnalysisStepKey) => Promise<boolean>;
  readonly working?: boolean;
}): ReactNode {
  const stages: StageItem[] = progress.steps.map((step) => {
    const content = ANALYSIS_STEP_CONTENT[step.key];
    const status: StageItem['status'] =
      step.status === 'completed'
        ? 'complete'
        : step.status === 'failed'
          ? 'failed'
          : step.status === 'queued'
            ? 'queued'
            : 'active';
    const meta =
      step.status === 'completed'
        ? copy.line.complete
        : step.status === 'failed'
          ? copy.line.stopped
          : step.status === 'queued'
            ? copy.line.queued
            : step.status === 'delayed'
              ? copy.line.delayed
              : step.status === 'recovering'
                ? copy.line.recovering
                : copy.line.inProgress;
    const findings = step.findings ?? [];
    return {
      key: step.key,
      title: content.title[language],
      desc: step.status === 'completed' ? undefined : content.desc[language],
      status,
      meta,
      checking:
        step.status === 'completed'
          ? undefined
          : content.checking.map((item) => item[language]),
      checkingLabel: copy.line.whatChecking,
      children: (
        <>
          {step.status === 'completed' ? (
            <details
              className="mj-ws-details mj-ws-sm"
              open={findings.length > 0 && step.key === lastCompletedKey(progress)}
            >
              <summary>{copy.line.whatFound}</summary>
              {findings.length === 0 ? (
                <p className="mj-ws-muted mj-ws-mt-8">{copy.line.noFindings}</p>
              ) : (
                <ul className="mj-ws-findings" aria-label={copy.line.whatFound}>
                  {findings.map((finding, index) => (
                    <li
                      key={`${finding.summary}-${String(index)}`}
                      className="mj-ws-finding"
                    >
                      <span className="mj-ws-finding-kind">
                        {EVIDENCE_KIND[finding.evidenceKind][language]}
                      </span>
                      <div>{finding.summary}</div>
                      {finding.sourceReferences.length > 0 ? (
                        <div className="mj-ws-finding-sources">
                          <span>{copy.line.source}</span>
                          {finding.sourceReferences.map((source) => (
                            <a
                              key={source.url}
                              href={source.url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {source.label}
                            </a>
                          ))}
                        </div>
                      ) : null}
                      {finding.limitation !== null ? (
                        <div className="mj-ws-finding-limitation">
                          {localizeMerchantFacingText(finding.limitation, language)}
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </details>
          ) : null}
          {step.status === 'failed' && step.retry.visible ? (
            <div className="mj-ws-row mj-ws-mt-8">
              <Button
                small
                disabled={working || !step.retry.authorized || onRetry === undefined}
                aria-describedby={
                  !step.retry.authorized ? `retry-${step.key}` : undefined
                }
                onClick={() => {
                  if (step.retry.authorized && onRetry !== undefined)
                    void onRetry(step.key);
                }}
              >
                {copy.line.retryStep}
              </Button>
              {!step.retry.authorized ? (
                <span id={`retry-${step.key}`} className="mj-ws-muted mj-ws-xs">
                  {copy.line.noRetry}
                </span>
              ) : null}
            </div>
          ) : null}
          {step.status !== 'queued' && step.status !== 'completed' ? (
            <div className="mj-ws-muted mj-ws-xs mj-ws-mt-8">
              {step.startedAt !== null
                ? `${copy.common.started(formatTime(step.startedAt, language))} · `
                : ''}
              {copy.common.updated(formatTime(step.lastConfirmedAt, language))}
            </div>
          ) : null}
        </>
      ),
    };
  });
  return <StageList stages={stages} />;
}

function lastCompletedKey(
  progress: MerchantAnalysisProgress,
): MerchantAnalysisStepKey | null {
  const completed = progress.steps.filter((step) => step.status === 'completed');
  return completed.at(-1)?.key ?? null;
}
